<template>
  <v-dialog
    :value="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    persistent
    :width="$store.state.common.largeDialogWidth"
    lazy
    @input="(v) => $emit('update:show', v)"
  >
    <v-card>
      <v-card-title class="title pb-0">
        <v-layout align-center>
          <v-icon
            medium
            class="msr--text mr-2"
          >
            auto_fix_high
          </v-icon>
          {{ $t('tvtemplatesProduceTvspot') }}
          <v-btn
            class="ml-auto mr-0 my-0"
            :disabled="busy"
            flat
            icon
            :title="$t('close')"
            @click="close()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <!-- Title for new TVSpot -->
        <v-text-field
          ref="title"
          v-model="titleForNewSpot"
          :label="$t('tvtemplatesProduceTvspotTitle')"
          :placeholder="titlePlaceholder"
          :rules="[rules.titleMinLength, rules.titleMaxLength]"
          :counter="titleMaxLength"
          :hint="$t('tvtemplatesProduceTvspotTitleHint') + '.'"
          persistent-hint
          class="mt-4"
          data-cy="title-input"
          clearable
          @keyup.esc="titleForNewSpot = ''"
          @keyup.enter="disableConfirmButton ? null : confirm()"
          @click:clear="() => (titleForNewSpot = '')"
        />
        <!-- Uploadgroup for new TVSpot -->
        <upload-group-select
          :selected-upload-group-id.sync="selectedUploadGroupId"
          :group-filter="filterUploadgroupsByContentCreatorPlan"
          class="mt-3"
        />
        <!-- Warnings for fields that are marked as 'default_value_must_be_changed' -->
        <div
          v-if="warnings.length > 0"
          class="mt-4"
        >
          <div
            v-for="(warning, warningIndex) in warnings"
            :key="'warning_' + warningIndex"
            class="my-2"
          >
            <v-layout align-center>
              <v-icon
                color="warning"
                class="mr-2"
              >
                error_outline
              </v-icon>
              <span class="warning--text">{{ warning.prefix }}:</span>&nbsp;
              <span class="body-2 warning--text">{{ warning.fieldLabel }}</span>
            </v-layout>
          </div>
        </div>
        <!-- Tags -->
        <v-slide-x-transition>
          <v-layout
            v-if="showTagSelect"
            align-center
            wrap
            class="mt-4"
          >
            <tag-select
              :tags="tagsForChosenUploadGroupNotYetUsed"
              button-icon-class="msr--text"
              button-text-class="grey--text text--darken-3 my-0 ml-0 mr-3 pa-0"
              :button-label="$t('tags')"
              :show-manage-tags-link="false"
              show-extra-divider
              @add="(tag) => addTagToSelection(tag)"
            >
              <div slot="extra-tile">
                <v-list-tile
                  class="grey--text text--darken-2"
                  style="text-transform: uppercase"
                  @click="clearTagSelection()"
                >
                  {{ $t('clearTags') }}
                </v-list-tile>
              </div>
            </tag-select>
            <tag-select-list
              :tags="tags"
              removable
              @remove="(tag) => removeTagFromSelection(tag)"
            />
          </v-layout>
        </v-slide-x-transition>
        <!-- Category -->
        <v-layout
          align-center
          wrap
          :class="[showTagSelect ? 'mt-3' : 'mt-4']"
        >
          <v-menu
            v-if="selectedUploadGroupId"
            offset-y
          >
            <v-btn
              slot="activator"
              round
              flat
              class="ma-0 pa-2 mr-3"
            >
              <v-icon
                left
                color="msr"
              >
                folder
              </v-icon>
              <div
                v-if="category"
                class="text-xs-left"
              >
                <div
                  class="caption"
                  style="font-size: 8px"
                >
                  {{ $t('category') }}
                </div>
                {{ category.name }}
              </div>
            </v-btn>
            <v-list>
              <v-list-tile
                v-for="assignableCategory in assignableCategories"
                :key="assignableCategory.id"
                @click="assignCategory(assignableCategory)"
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ assignableCategory.name }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>
        <!-- Hard-Range -->
        <v-slide-x-transition>
          <v-layout
            v-if="selectedUploadGroupId"
            align-center
            wrap
            class="mt-3"
          >
            <v-btn
              round
              flat
              class="ma-0 pa-2 mr-3"
              @click="showEditHardRangeDialog = !showEditHardRangeDialog"
            >
              <v-icon
                left
                color="msr"
              >
                date_range
              </v-icon>
              {{ $t('contentHardRangeSet') }}
            </v-btn>
            <div
              class="warning--text clickable"
              @click="showEditHardRangeDialog = true"
            >
              <span v-if="hardStart">
                <span v-if="!hardExpire">{{ $t('hardStart') }}</span>
                {{ hardStart | dayjs('DD.MM.YYYY') }}
              </span>
              <span v-if="!!hardStart && !!hardExpire"> - </span>
              <span v-if="hardExpire">
                <span v-if="!hardStart">{{ $t('hardExpire') }}</span>
                {{ hardExpire | dayjs('DD.MM.YYYY') }}
              </span>
            </div>
            <edit-hard-range-dialog
              :show.sync="showEditHardRangeDialog"
              :hard-expire="hardExpire"
              :hard-start="hardStart"
              :hard-expire-offset="false"
              @confirm="assignHardRange"
            />
          </v-layout>
        </v-slide-x-transition>
        <!-- Published or unpublished -->
        <v-slide-x-transition>
          <v-layout
            v-if="selectedUploadGroupId"
            align-center
            class="mt-1 clickable lightgrey--hover"
            @click.stop="produceAsPublished = !produceAsPublished"
          >
            <v-btn
              icon
              large
              class="ml-0"
            >
              <v-icon
                v-if="produceAsPublished"
                color="msr"
              >
                visibility
              </v-icon>
              <v-icon
                v-else
                color="grey"
              >
                visibility_off
              </v-icon>
            </v-btn>
            <span class="caption grey--text text--darken-2">
              {{
                produceAsPublished
                  ? $t('produceContentAsPublished')
                  : $t('produceContentAsUnpublished')
              }}.
            </span>
          </v-layout>
        </v-slide-x-transition>
        <!-- Group Content Overlay Image -->
        <v-slide-x-transition>
          <v-layout
            v-if="contentOverlayImage"
            column
            class="mt-3"
          >
            <div class="caption grey--text text--darken-2 mb-2 ml-auto">
              {{ $t('contentUploadGroupContentImageOverlayHint') }}:
            </div>
            <img
              class="unselectable ml-auto"
              :src="contentOverlayImage.image_url"
              style="max-width: 72px; max-height: 72px; object-fit: contain"
            >
          </v-layout>
        </v-slide-x-transition>
      </v-card-text>
      <v-card-actions>
        <v-layout justify-center>
          <btn-ok
            :loading="busy"
            :disabled="disableConfirmButton"
            :label="$t('tvtemplatesProduceTvspotButtonConfirm')"
            round
            data-cy="confirm-produce-spot-btn"
            @click="confirm()"
          />
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {DEFAULT_SPOTCATEGORY_NAME} from '@/store';
import uploadGroupSelect from '@/mixins/uploadGroupSelect';

import BtnOk from '@/components/ButtonOk';
import EditHardRangeDialog from '@/components/EditHardRangeDialog';
import TagSelect from '@/components/TagSelect';
import TagSelectList from '@/components/TagSelectList';
import UploadGroupSelect from '@/components/UploadGroupSelect';

export default {
  components: {
    BtnOk,
    EditHardRangeDialog,
    UploadGroupSelect,
    TagSelect,
    TagSelectList,
  },
  mixins: [uploadGroupSelect],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    disableConfirmButton: {
      type: Boolean,
      default: false,
    },
    titleMinLength: {
      type: Number,
      required: true,
    },
    titleMaxLength: {
      type: Number,
      required: true,
    },
    /**
     * Each warning is: {'prefix: String, 'fieldLabel': String'}
     */
    warnings: {
      type: Array[Object],
      default: () => [],
    },
    /**
     * Below: Actual user input we care about.
     *
     * These are meant to be passed with .sync as we emit updates here.
     */
    title: {
      type: String,
      default: null,
    },
    published: {
      type: Boolean,
      default: null,
    },
    tags: {
      type: Array[Object],
      default: null,
    },
    category: {
      type: Object,
      default: null,
    },
    hardStart: {
      type: [Date, String],
      default: null,
    },
    hardExpire: {
      type: [Date, String],
      default: null,
    },
    titlePlaceholder: {
      type: String,
      default: null,
    },
    groupId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      showEditHardRangeDialog: false,
    };
  },
  computed: {
    contentOverlayImage() {
      if (!this.selectedUploadGroupId) {
        return null;
      }
      return this.selectedUploadGroup.content_overlay_image;
    },
    showTagSelect() {
      return (
        this.$userprofile &&
        this.$userprofile.can_use_tagging &&
        this.selectedUploadGroupId &&
        this.tagsForChosenUploadGroup.length > 0
      );
    },
    assignableCategories() {
      return this.$store.state.common.categories.filter(
        (category) => !category.is_closingtime
      );
    },
    defaultCategory() {
      const byName = this.assignableCategories.filter(
        (category) =>
          category.is_default && category.name === DEFAULT_SPOTCATEGORY_NAME
      )[0];
      if (byName) {
        return byName;
      }
      return null;
    },
    titleForNewSpot: {
      get() {
        return this.title;
      },
      set(title) {
        this.$emit('update:title', title);
      },
    },
    selectedUploadGroupId: {
      get() {
        return this.groupId;
      },
      set(groupId) {
        this.$emit('update:groupId', groupId);
      },
    },
    tagsForChosenUploadGroup() {
      if (!this.selectedUploadGroupId) {
        return [];
      }
      return this.$store.state.common.tags.filter((tag) => {
        return tag.group === this.selectedUploadGroupId;
      });
    },
    tagsForChosenUploadGroupNotYetUsed() {
      return this.tagsForChosenUploadGroup.filter((tag) => {
        for (const chosenTag of this.tags) {
          if (chosenTag.id === tag.id) {
            return false;
          }
        }
        return true;
      });
    },
    produceAsPublished: {
      get() {
        return this.published;
      },
      set(published) {
        this.$emit('update:published', published);
      },
    },
    rules() {
      const vm = this;
      return {
        titleMinLength(v) {
          return (
            (!!v && v.length >= vm.titleMinLength) || vm.$t('titleTooShort')
          );
        },
        titleMaxLength(v) {
          return (
            (!!v && v.length <= vm.titleMaxLength) || vm.$t('titleTooLong')
          );
        },
      };
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        if (this.warnings.length === 0) {
          this.$nextTick(() => {
            this.$refs.title.focus();
          });
        }
        this.preselectDefaultCategory();
      }
    },
    assignableCategories() {
      this.preselectDefaultCategory();
    },
    selectedUploadGroupId: function () {
      this.clearTagSelection();
    },
  },
  methods: {
    preselectDefaultCategory() {
      if (!this.category) {
        this.assignCategory(this.defaultCategory);
      }
    },
    assignCategory(category) {
      this.$emit('update:category', category);
    },
    assignHardRange(opts) {
      this.$emit('update:hardStart', opts.newHardStart);
      this.$emit('update:hardExpire', opts.newHardExpire);
    },
    filterUploadgroupsByContentCreatorPlan() {
      if (this.$userprofile) {
        return this.uploadgroups.filter((group) => {
          if (group.kind === 'ASSOCIATION') {
            // Association Groups should only be targettable by
            // association Users (aka within an association Workspace)
            // or when the appropriate Enterprise plan has been booked.
            const isAssociationWorkspaceUser =
              this.$userprofile &&
              this.$userprofile.workspace &&
              this.$userprofile.workspace.contains_association_groups;
            const hasAssociationPlan =
              this.$userprofile &&
              this.$userprofile.content_creator_plan &&
              this.$userprofile.content_creator_plan
                .can_target_association_groups;
            return isAssociationWorkspaceUser || hasAssociationPlan;
          }

          return true;
        });
      }
      return this.uploadgroups;
    },
    addTagToSelection(tag) {
      for (const existingTag of this.tags) {
        if (existingTag.id === tag.id) {
          return;
        }
      }

      const newTags = this.tags.slice();
      newTags.push(tag);

      this.$emit('update:tags', newTags);
    },
    removeTagFromSelection(tag) {
      const index = this.tags.findIndex((t) => t.id === tag.id);
      if (index >= 0) {
        const newTags = this.tags.slice();
        newTags.splice(index, 1);

        this.$emit('update:tags', newTags);
      }
    },
    clearTagSelection() {
      this.$emit('update:tags', []);
    },
    close() {
      this.$emit('update:show', false);
    },
    confirm() {
      this.$emit('confirm');
    },
  },
};
</script>

<style scoped>
.lightgrey--hover:hover {
  background: rgba(0, 0, 0, 0.04);
}
</style>



// WEBPACK FOOTER //
// src/components/TvTemplateProductionDialog.vue